"use client";
import { WEB_COLORS } from "@/lib/constants";
import { Stack, useMediaQuery, useTheme } from "@mui/material";
import { useEffect, useRef, useState } from "react";

function OtpInput({
  length = 4,
  onOtpSubmit = () => {},
  isLoading,
  error,
  disableAutoSubmit = false,
  handleOtpOnChange,
}) {
  const [otp, setOtp] = useState(new Array(length).fill(""));
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const inputRefs = useRef([]);

  const handleOtpChange = (index, e) => {
    // Keep only digits; drop letters, e/E, +, -, ., spaces, symbols
    const digitsOnly = e.target.value.replace(/\D/g, "");

    const newOtp = [...otp];

    if (!digitsOnly) {
      newOtp[index] = "";
      setOtp(newOtp);
      handleOtpOnChange?.({ value: newOtp.join(""), completed: false });
      return;
    }

    // Take the last typed digit so typing over an existing digit replaces it
    newOtp[index] = digitsOnly.slice(-1);
    setOtp(newOtp);

    const combinedOtp = newOtp.join("");
    handleOtpOnChange?.({
      value: combinedOtp,
      completed: combinedOtp.length === length,
    });

    if (!disableAutoSubmit && combinedOtp.length === length) {
      onOtpSubmit(combinedOtp);
    }

    if (index < length - 1 && inputRefs.current[index + 1]) {
      inputRefs.current[index + 1].focus();
    }
  };

  const handleClick = (index) => {
    const input = inputRefs?.current[index];
    if (input) {
      input.setSelectionRange(0, input.value.length);
    }
  };

  const handleKeyDown = (index, e) => {
    // Allow common control keys
    const allowedKeys = [
      "Backspace",
      "Delete",
      "Tab",
      "ArrowLeft",
      "ArrowRight",
      "Home",
      "End",
    ];

    // Allow copy/paste/cut/select-all
    if (
      (e.ctrlKey || e.metaKey) &&
      ["a", "c", "v", "x"].includes(e.key.toLowerCase())
    ) {
      return;
    }

    if (allowedKeys.includes(e.key)) {
      if (
        e.key === "Backspace" &&
        !otp[index] &&
        index > 0 &&
        inputRefs.current[index - 1]
      ) {
        inputRefs.current[index - 1].focus();
      }
      return;
    }

    // Block anything that isn't a single digit
    if (!/^[0-9]$/.test(e.key)) {
      e.preventDefault();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pasted = (e.clipboardData.getData("text") || "").replace(/\D/g, "");
    if (!pasted) return;

    const newOtp = [...otp];
    for (let i = 0; i < length; i++) {
      newOtp[i] = pasted[i] ?? "";
    }
    setOtp(newOtp);

    const combinedOtp = newOtp.join("");
    handleOtpOnChange?.({
      value: combinedOtp,
      completed: combinedOtp.length === length,
    });

    const nextIndex = Math.min(pasted.length, length - 1);
    inputRefs.current[nextIndex]?.focus();

    if (!disableAutoSubmit && combinedOtp.length === length) {
      onOtpSubmit(combinedOtp);
    }
  };

  useEffect(() => {
    if (inputRefs.current[0]) {
      inputRefs.current[0].focus();
    }
  }, []);

  return (
    <Stack direction="row" justifyContent="center" spacing={2} px={2}>
      {otp?.map((value, index) => {
        const isDisabled =
          isLoading && otp.filter((v) => v !== "").length === length;
        return (
          <input
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            autoComplete="one-time-code"
            maxLength={1}
            ref={(input) => (inputRefs.current[index] = input)}
            key={index}
            value={value}
            onChange={(e) => handleOtpChange(index, e)}
            onClick={() => handleClick(index)}
            onKeyDown={(e) => handleKeyDown(index, e)}
            onPaste={handlePaste}
            disabled={isDisabled}
            style={{
              border: error
                ? "1px solid red"
                : `1px solid ${WEB_COLORS.disabledColor}`,
              width:
                length >= 6 && isMobile ? "25px" : isMobile ? "40px" : "50px",
              height:
                length >= 6 && isMobile ? "25px" : isMobile ? "40px" : "50px",
              textAlign: "center",
              fontSize: isMobile ? "16px" : "1.3em",
              backgroundColor: "rgba(242, 247, 247, 0.8)",
              borderRadius: "6px",
            }}
          />
        );
      })}
    </Stack>
  );
}

export default OtpInput;